/* eslint-disable no-restricted-syntax */
/**
 * Field Matching Utilities for Filevine Entity Sync
 *
 * Pure utility functions for:
 * - Parsing suggested values into structured display forms
 * - Matching field names between affected_fields and field configs
 * - Extracting suggested values from payloads
 */

import type {
  FilevineSectionFieldConfigSchema,
  FilevineSectionPayload,
} from '@/services/api/generated/entitySyncService';

// ============================================================================
// Types
// ============================================================================

/**
 * Discriminated union representing parsed suggested values for display.
 * Enables type-safe rendering of different suggestion types.
 */
export type ParsedSuggestedValue =
  | { type: 'text'; text: string }
  | { type: 'contact_link'; text: string; contactId: number }
  | { type: 'contact_create'; text: string; contactName?: string }
  | { type: 'clear'; text: '(clear)' };

// ============================================================================
// Suggested Value Parsing
// ============================================================================

/**
 * Parse a suggested value into structured data for rendering.
 * Handles special cases like contact link objects.
 */
export function parseSuggestedValue(value: unknown): ParsedSuggestedValue {
  if (value === null || value === '') {
    return { type: 'clear', text: '(clear)' };
  }

  if (value === undefined) {
    return { type: 'text', text: '—' };
  }

  // Handle contact link objects: {"action": "LINK_EXISTING_CONTACT", "contact_id": 123}
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (
      obj.action === 'LINK_EXISTING_CONTACT' &&
      typeof obj.contact_id === 'number'
    ) {
      return {
        type: 'contact_link',
        text: `Link to contact #${obj.contact_id}`,
        contactId: obj.contact_id,
      };
    }
    if (obj.action === 'CREATE_NEW_CONTACT' && obj.payload) {
      const payload = obj.payload as Record<string, unknown>;
      const name = [payload.first_name, payload.last_name]
        .filter(Boolean)
        .join(' ');
      return {
        type: 'contact_create',
        text: name ? `Create contact: ${name}` : 'Create new contact',
        contactName: name || undefined,
      };
    }
    // Generic object - try to make it readable
    return { type: 'text', text: JSON.stringify(value) };
  }

  // Handle arrays (e.g., multi-select values)
  if (Array.isArray(value)) {
    if (value.length === 0) return { type: 'text', text: '—' };
    return { type: 'text', text: value.map((v) => String(v)).join(', ') };
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return { type: 'text', text: value ? 'Yes' : 'No' };
  }

  return { type: 'text', text: String(value) };
}

/**
 * Format a suggested value for display (simple text version).
 * Used when we don't need rich rendering.
 */
export function formatSuggestedValue(value: unknown): string {
  return parseSuggestedValue(value).text;
}

// ============================================================================
// Payload Extraction
// ============================================================================

/**
 * Extract the updates dict from a suggested payload.
 */
export function getUpdatesFromPayload(
  suggestedPayload: FilevineSectionPayload | undefined,
): FilevineSectionPayload['updates'] | undefined {
  if (!suggestedPayload) return undefined;
  return suggestedPayload.updates;
}

// ============================================================================
// Field Matching
// ============================================================================

/**
 * Check if a field is affected by looking at both internal_name and field_selector.
 *
 * affected_fields may use field_selector format (e.g., "provider") while
 * internal_name has the section ID suffix (e.g., "provider425142").
 *
 * This function handles:
 * - Exact internal_name match
 * - Case-insensitive internal_name match
 * - field_selector match (without path)
 * - Prefix matching with numeric suffix detection
 */
export function isFieldAffected(
  fc: FilevineSectionFieldConfigSchema,
  affectedFields: Set<string>,
): boolean {
  // Check internal_name (exact match)
  if (affectedFields.has(fc.internal_name)) return true;

  // Check lowercase version of internal_name
  if (affectedFields.has(fc.internal_name.toLowerCase())) return true;

  // Check field_selector (without path, e.g., "provider" from "section/provider")
  const selectorParts = fc.field_selector.split('/');
  const fieldName = selectorParts[selectorParts.length - 1];
  if (affectedFields.has(fieldName)) return true;
  if (affectedFields.has(fieldName.toLowerCase())) return true;

  // Also check if any affected field matches the start of internal_name
  // (handles case where affected_fields uses field_selector but internal_name has suffix)
  for (const affected of affectedFields) {
    const lowerAffected = affected.toLowerCase();
    const lowerInternal = fc.internal_name.toLowerCase();
    // Check if internal_name starts with the affected field name
    if (
      lowerInternal.startsWith(lowerAffected) &&
      lowerInternal !== lowerAffected
    ) {
      // Verify the rest is just digits (the section ID suffix)
      const suffix = lowerInternal.slice(lowerAffected.length);
      if (/^\d+$/.test(suffix)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get the suggested value for a field from the payload.
 *
 * Handles both internal_name format (with section ID suffix) and field_selector format.
 * Uses multiple lookup strategies:
 * - Exact internal_name match
 * - Case-insensitive internal_name match
 * - field_selector match
 * - Case-insensitive field_selector match
 */
export function getSuggestedValue(
  fc: FilevineSectionFieldConfigSchema,
  suggestedPayload: FilevineSectionPayload | undefined,
): unknown {
  const updates = getUpdatesFromPayload(suggestedPayload);
  if (!updates) return undefined;

  // Try internal_name (exact match - this is what the backend uses)
  if (fc.internal_name in updates) {
    return updates[fc.internal_name];
  }

  // Try lowercase version of internal_name
  const lowerInternalName = fc.internal_name.toLowerCase();
  for (const [key, value] of Object.entries(updates)) {
    if (key.toLowerCase() === lowerInternalName) {
      return value;
    }
  }

  // Try field_selector (without path)
  const selectorParts = fc.field_selector.split('/');
  const fieldName = selectorParts[selectorParts.length - 1];
  if (fieldName in updates) {
    return updates[fieldName];
  }

  // Try lowercase field_selector
  for (const [key, value] of Object.entries(updates)) {
    if (key.toLowerCase() === fieldName.toLowerCase()) {
      return value;
    }
  }

  return undefined;
}
